import { CardContent, Grid, CardMedia, Paper, List } from '@material-ui/core';
import React from 'react';
import moment from 'moment-timezone';
import { Alert } from '@material-ui/lab';
import {
    checkBookerFieldsAreOk,
    checkCustomBookingFieldsAreOk,
    checkGuestFieldsAreOk,
    getBookerFieldsFromCartitems
} from '../../../guestfields';
import CartItemInfo from './CartItemInfo';
import { CalendarDayIcon, ClockIcon, LocationIcon, TicketIcon } from '../../../icons';
import OverlappingActivity from './OverlappingActivity';
import {
    calculateOverlappingActivities,
    checkOutsideStayPeriod
} from '../../../utils/bookingValidation';

function CartItemAddons({ cartItem }) {
    if (!cartItem.addons || cartItem.addons.length === 0) {
        return null;
    }

    return (
        <Grid item>
            <Paper className="colorcard padded" elevation={2}>
                <CartItemInfo noIcon title="Add-on services" value={' '} />
                {cartItem?.addons?.map(addon => (
                    <CartItemInfo
                        key={addon.id}
                        noIcon
                        value={`${addon.name} ${addon.quantity}x`}
                    />
                ))}
            </Paper>
        </Grid>
    );
}

function CartItem({
    user,
    customer,
    cartItem,
    booker,
    isAgent,
    bookingsData,
    showWarning = true,
    showImage = true,
    children,
    hasErrors
}) {
    // booker fields needs to be collected for ALL tickets (even if they are not connected to the cartitem)
    const bookerFields = getBookerFieldsFromCartitems([cartItem]);
    const bookerRequiredFieldsOk = checkBookerFieldsAreOk(booker, bookerFields, isAgent);

    // checking every cartitem, within that every ticket, with every ticket every guest
    const guestsRequiredFieldsOk = checkGuestFieldsAreOk([cartItem], isAgent);

    // check if agent filled out every custom booking field
    const customBookingRequiredFieldsOk = checkCustomBookingFieldsAreOk([cartItem]);

    const fieldProblems =
        !bookerRequiredFieldsOk || !guestsRequiredFieldsOk || !customBookingRequiredFieldsOk;

    // create location string
    let location = '';
    if (cartItem?.activity?.island?.name) {
        location = cartItem?.activity?.island?.name;
    }
    if (cartItem?.activity?.extra_info?.location) {
        const loc = cartItem?.activity?.extra_info?.location;
        if ((loc?.street || '') !== '' || (loc?.city || '') !== '') {
            location += `, ${loc?.city || ''} ${loc?.street || ''}`;
        }
    }

    // images
    let image = cartItem?.activity?.image || '';
    if (
        image === '' &&
        cartItem?.activity?.extra_info?.images &&
        cartItem?.activity?.extra_info?.images.length > 0
    ) {
        image = cartItem?.activity?.extra_info?.images[0];
    }

    const showErrors = {
        width: 'inherit',
        border: '3px solid red'
    };

    // Calculate validation results using bookings data from parent
    const outsideOfStayPeriod = checkOutsideStayPeriod(cartItem, user, customer);
    const overlappingActivities = calculateOverlappingActivities(cartItem, bookingsData);
    const isDateConflict = overlappingActivities.length > 0;

    return (
        <Paper className="colorcard cartitem" elevation={0} style={hasErrors ? showErrors : {}}>
            {image && showImage && <CardMedia image={image} className="cartitem" />}
            <CardContent style={{ maxWidth: '100%' }}>
                <Grid style={{ width: '100%' }} container spacing={2} direction="column">
                    <Grid item>
                        <h2>{cartItem?.activity?.name}</h2>
                        <CartItemInfo icon={<LocationIcon />} title="Location" value={location} />
                        <CartItemInfo
                            icon={<CalendarDayIcon />}
                            title="Date"
                            value={moment(cartItem.start_time).format('MMMM Do YYYY')}
                        />
                        <CartItemInfo
                            icon={<ClockIcon className="fa-lg" />}
                            title="Time"
                            value={moment(cartItem.start_time).format('h:mm a')}
                        />
                        <CartItemInfo icon={<TicketIcon />} title="Tickets" value={' '} />
                        {cartItem?.tickets?.map(ticket => (
                            <CartItemInfo
                                key={ticket.id}
                                value={`${ticket.name} ${ticket.quantity}x`}
                            />
                        ))}
                    </Grid>
                    <CartItemAddons cartItem={cartItem} />
                    {outsideOfStayPeriod && (
                        <Grid item>
                            <Alert severity="error">
                                This activity is outside of your stay period.
                            </Alert>
                        </Grid>
                    )}
                    {isDateConflict && (
                        <Grid container item xs={12} spacing={2} direction="column">
                            <Grid item>
                                <Alert severity="error">
                                    Please check your bookings, the following activities are
                                    overlapping or touching with this activity:
                                </Alert>
                                <List
                                    style={{
                                        maxHeight: 150,
                                        overflow: 'auto',
                                        padding: 5
                                    }}
                                >
                                    {overlappingActivities.map((activity, index) => (
                                        <OverlappingActivity key={index} activity={activity} />
                                    ))}
                                </List>
                            </Grid>
                        </Grid>
                    )}
                    {fieldProblems && showWarning && (
                        <Grid item>
                            <Alert severity="warning" style={{ width: '100%' }}>
                                There are some missing fields for this activity.
                            </Alert>
                        </Grid>
                    )}
                    {children}
                </Grid>
            </CardContent>
        </Paper>
    );
}

export default CartItem;
